/**
 * Inductor — four-bump coil (IEEE 315 humped form).
 *
 *     p1 ──∩∩∩∩── p2
 *
 * Local frame: 80×20. Pins on the centreline at (0,16) and (80,16).
 * Stubs at the left and right; four half-circle bumps between
 * x=16 and x=64, each spanning 12 px wide × 12 px tall.
 *
 * The humped form is the modern IEEE convention and visually distinct
 * from a resistor's zigzag at small zooms — important when the canvas
 * is rendering 50+ passives at once.
 */
import type { PinAnchor, SymbolArtworkProps, SymbolDefinition } from "./types";

const W = 80;
const H = 20;
const MID = 16;
const STUB_LEFT = 16;
const STUB_RIGHT = 64;
const HUMP_WIDTH = (STUB_RIGHT - STUB_LEFT) / 4; // 12
const HUMP_HEIGHT = 10; // peak above MID

function bumpsPath(): string {
  const parts = [`M ${STUB_LEFT} ${MID}`];
  for (let i = 0; i < 4; i++) {
    const x0 = STUB_LEFT + i * HUMP_WIDTH;
    const x1 = x0 + HUMP_WIDTH;
    const cx = x0 + HUMP_WIDTH / 2;
    // Quadratic Bézier to draw a half-circle-ish bump above MID.
    parts.push(`Q ${cx} ${MID - HUMP_HEIGHT * 1.5} ${x1} ${MID}`);
  }
  return parts.join(" ");
}

const PINS: Record<string, PinAnchor> = {
  p1: { x: 0, y: MID, side: "WEST" },
  p2: { x: W, y: MID, side: "EAST" },
};

const BUMP_D = bumpsPath();

export function InductorArtwork({
  stroke,
  strokeWidth = 1.4,
}: SymbolArtworkProps) {
  return (
    <g>
      <line
        x1={0}
        y1={MID}
        x2={STUB_LEFT}
        y2={MID}
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <path
        d={BUMP_D}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <line
        x1={STUB_RIGHT}
        y1={MID}
        x2={W}
        y2={MID}
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </g>
  );
}

export const InductorSymbol: SymbolDefinition = {
  key: "inductor",
  bbox: { w: W, h: H },
  pinAnchors: PINS,
  Artwork: InductorArtwork,
};
